"use client";

import * as React from "react";
import { useRouter } from "@/lib/router-compat";
import { authRepository } from "@/repository/authRepository";
import { localAuthRepository } from "@/repository/localAuthRepository";
import {
  getDefaultStaffMode,
  hasPlatformAccess,
  hasTenantAccess,
  isStaffUiMode,
  type StaffUiMode,
} from "@/lib/staffMode";
import type { UserInfo } from "@/types/common";

const STORAGE_KEY = "sumacomi:staff-ui-mode:v1";

type StaffModeContextValue = {
  mode: StaffUiMode;
  canUseTenantMode: boolean;
  canUsePlatformMode: boolean;
  isSwitching: boolean;
  switchMode: (nextMode: StaffUiMode) => Promise<void>;
};

const StaffModeContext = React.createContext<StaffModeContextValue | null>(
  null
);

function readStoredMode(userInfo: UserInfo | null): StaffUiMode {
  if (typeof window === "undefined") return getDefaultStaffMode(userInfo);
  const stored = window.sessionStorage.getItem(STORAGE_KEY);
  if (
    isStaffUiMode(stored) &&
    ((stored === "tenant" && hasTenantAccess(userInfo)) ||
      (stored === "platform" && hasPlatformAccess(userInfo)))
  ) {
    return stored;
  }
  return getDefaultStaffMode(userInfo);
}

export function StaffModeProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [userInfo, setUserInfo] = React.useState<UserInfo | null>(() =>
    localAuthRepository.getUserInfo()
  );
  const [mode, setMode] = React.useState<StaffUiMode>(() =>
    readStoredMode(userInfo)
  );
  const [isSwitching, setIsSwitching] = React.useState(false);

  const canUseTenantMode = hasTenantAccess(userInfo);
  const canUsePlatformMode = hasPlatformAccess(userInfo);

  React.useEffect(() => {
    const syncAuth = () => {
      const nextUser = localAuthRepository.getUserInfo();
      setUserInfo(nextUser);
      setMode(readStoredMode(nextUser));
    };
    window.addEventListener("authStateChanged", syncAuth);
    return () => window.removeEventListener("authStateChanged", syncAuth);
  }, []);

  React.useEffect(() => {
    document.documentElement.dataset.staffMode = mode;
    return () => {
      delete document.documentElement.dataset.staffMode;
    };
  }, [mode]);

  const switchMode = React.useCallback(
    async (nextMode: StaffUiMode) => {
      if (nextMode === mode) return;
      if (
        (nextMode === "tenant" && !canUseTenantMode) ||
        (nextMode === "platform" && !canUsePlatformMode)
      ) {
        throw new Error("このモードを利用する権限がありません");
      }

      setIsSwitching(true);
      try {
        await authRepository.recordModeSwitch({
          fromMode: mode,
          toMode: nextMode,
        });
        window.sessionStorage.setItem(STORAGE_KEY, nextMode);
        setMode(nextMode);
        router.push(
          nextMode === "platform" ? "/operator/tenant-payments" : "/dashboard"
        );
      } finally {
        setIsSwitching(false);
      }
    },
    [canUsePlatformMode, canUseTenantMode, mode, router]
  );

  const value = React.useMemo(
    () => ({
      mode,
      canUseTenantMode,
      canUsePlatformMode,
      isSwitching,
      switchMode,
    }),
    [mode, canUseTenantMode, canUsePlatformMode, isSwitching, switchMode]
  );

  return (
    <StaffModeContext.Provider value={value}>
      {children}
    </StaffModeContext.Provider>
  );
}

export function useStaffMode(): StaffModeContextValue {
  const context = React.useContext(StaffModeContext);
  if (!context) {
    throw new Error("useStaffMode must be used within StaffModeProvider");
  }
  return context;
}
